/**
 * Builds the request_context object sent with Vision AI createMessage requests.
 */

import { useFilterSidePanel } from '@dashhudson/dashing-ui/filter-side-panel';
import { computed, toValue } from 'vue';
import { useRoute } from 'vue-router';

import { useCommunityVisionAiRequestContext } from '@/components/VisionAi/composables/useCommunityVisionAiRequestContext';

export interface RequestContextRoute {
  name?: string | symbol | null;
}

export interface RequestContext {
  route?: RequestContextRoute;
  filters?: Record<string, unknown>;
  buttonIdentifier?: string;
  currentBrandId: number;
  /** Optional extra context (e.g. messageId for thread summarization). */
  extra?: Record<string, unknown>;
}

/**
 * @param route - Current route (from useRoute())
 * @param filters - Applied filters for the current view (e.g. intentQuery or subset)
 * @param buttonIdentifier - One of REQUEST_CONTEXT_BUTTON_IDENTIFIERS
 * @param currentBrandId - Current brand id
 * @param extra - Optional extra context (e.g. { messageId } for thread scope)
 */
export function buildRequestContext(
  route: RequestContextRoute | null | undefined,
  currentBrandId: number,
  filters?: Record<string, unknown> | null | undefined,
  buttonIdentifier?: string,
  extra?: Record<string, unknown>,
): RequestContext {
  return {
    route: route
      ? {
          name: route.name,
        }
      : undefined,
    filters: filters ?? {},
    buttonIdentifier,
    currentBrandId,
    ...(extra && Object.keys(extra).length > 0 && { extra }),
  };
}

function omitEmptyValues(obj: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(obj).filter(([, value]) => {
      if (value === null || value === undefined) return false;
      if (Array.isArray(value) && value.length === 0) return false;
      if (value === '') return false;
      return true;
    }),
  );
}

/**
 * Composable that returns a reactive `filters` object for the current page.
 *
 * Route-specific composables handle the transformation from raw side-panel
 * filters into the shape each Vision AI backend expects.
 */
export function usePageFilters() {
  const route = useRoute();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const filterSidePanel = useFilterSidePanel() as any;
  const { requestContextFilters: communityFilters } = useCommunityVisionAiRequestContext();

  return computed<Record<string, unknown>>(() => {
    const routeName = route?.name?.toString() ?? '';
    if (routeName.startsWith('community.')) {
      return omitEmptyValues(communityFilters.value);
    }

    const rawFilters = toValue(
      filterSidePanel?.scopedFilters ?? filterSidePanel?.filters,
    ) as Record<string, unknown>;
    if (!rawFilters) return {};
    return omitEmptyValues(rawFilters);
  });
}
